import { EVENT_SOUNDS, type EventSoundCategory } from "./soundManifest";

type SoundMode = "press" | "normal" | "big" | "mega" | "soft";

let audioContext: AudioContext | null = null;
let currentClip: HTMLAudioElement | null = null;
let queuedClip: string | null = null;
const clipCache = new Map<string, HTMLAudioElement>();

function getContext() {
  if (!audioContext) {
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioCtor();
  }
  return audioContext;
}

function tone(freq: number, start: number, duration: number, gain: number, type: OscillatorType) {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const amp = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(Math.min(2600, freq * 2.6), start);
  filter.Q.setValueAtTime(1.4, start);

  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.015);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(filter);
  filter.connect(amp);
  amp.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

function noise(start: number, duration: number, gainPeak: number) {
  const ctx = getContext();
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }

  const source = ctx.createBufferSource();
  const amp = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.setValueAtTime(900, start);
  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gainPeak, start + 0.01);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  source.buffer = buffer;
  source.connect(filter);
  filter.connect(amp);
  amp.connect(ctx.destination);
  source.start(start);
  source.stop(start + duration);
}

export async function playSound(mode: SoundMode) {
  const ctx = getContext();
  if (ctx.state === "suspended") await ctx.resume();

  const now = ctx.currentTime;
  if (mode === "press") {
    tone(180, now, 0.07, 0.08, "triangle");
    tone(360, now + 0.035, 0.08, 0.06, "sine");
    return;
  }

  if (mode === "normal" || mode === "soft") {
    tone(420, now, 0.12, 0.07, "sine");
    tone(630, now + 0.06, 0.16, 0.055, "triangle");
    return;
  }

  noise(now, mode === "mega" ? 0.42 : 0.28, mode === "mega" ? 0.075 : 0.052);
  tone(72, now, mode === "mega" ? 0.42 : 0.3, mode === "mega" ? 0.18 : 0.12, "sine");

  const notes = mode === "mega" ? [220, 330, 440, 660, 990, 1320] : [220, 330, 550, 770, 990];
  notes.forEach((note, index) => {
    tone(note, now + index * 0.045, mode === "mega" ? 0.42 : 0.32, 0.105, index % 2 ? "triangle" : "sawtooth");
  });
}

function pickRandom<T>(items: T[]): T | null {
  if (items.length === 0) return null;
  return items[Math.floor(Math.random() * items.length)];
}

export function installQueuedClipRetry() {
  const retry = () => {
    if (!queuedClip) return;
    const clip = queuedClip;
    queuedClip = null;
    void playClipUrl(clip);
  };

  window.addEventListener("pointerdown", retry, { passive: true });
  window.addEventListener("keydown", retry);
}

export function preloadEventSounds() {
  for (const urls of Object.values(EVENT_SOUNDS)) {
    for (const url of urls) {
      if (clipCache.has(url)) continue;
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.load();
      clipCache.set(url, audio);
    }
  }
}

async function playClipUrl(url: string): Promise<boolean> {
  try {
    currentClip?.pause();
    const cached = clipCache.get(url);
    currentClip = cached ? (cached.cloneNode(true) as HTMLAudioElement) : new Audio(url);
    currentClip.preload = "auto";
    currentClip.volume = 1;
    await currentClip.play();
    return true;
  } catch {
    queuedClip = url;
    return false;
  }
}

export async function playEventSound(category: EventSoundCategory): Promise<boolean> {
  const url = pickRandom(EVENT_SOUNDS[category]);
  if (!url) return false;
  return playClipUrl(url);
}
